/**
 * Die Abfragen, die mehr als ein Bereich braucht.
 *
 * Seit die Übersicht, die Geräteverwaltung und die Einstellungen getrennte
 * Bereiche sind, würden dieselben drei Listen sonst an fünf Stellen mit fünf
 * leicht verschiedenen Fehlermeldungen stehen. Der Schlüssel ist dabei die
 * eigentliche Verabredung: Wer `["devices"]` benutzt, bekommt denselben
 * zwischengespeicherten Stand wie alle anderen, und ein `invalidateQueries`
 * nach dem Beanspruchen eines Geräts erreicht jede Ansicht auf einmal.
 *
 * Kein Aufruf von Hand — alles über den erzeugten Client aus `@hausmeter/sdk`.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "./client.js";

/**
 * `enabled = false` für einen reinen Bewohner (HM-081): Für ihn antwortet der
 * Endpunkt mit `403`, und ein Fehler vor der ersten Ansicht wäre sein erster
 * Eindruck. Der Rahmen weiß es aus den Rollen in `GET /auth/me`.
 */
export function useSites(enabled = true) {
  return useQuery({
    queryKey: ["sites"],
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/sites", {});
      if (error || !data) throw new Error("Die Anlagen konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Geräte und Integrationen gehören einer **Anlage** (ADR 0017), nicht dem Konto.
 * Der Schlüssel trägt die Anlage deshalb mit: Ein Wechsel im Umschalter lädt die
 * Liste neu, statt die vorige weiterzuzeigen.
 *
 * `enabled`: Ohne Anlage gibt es nichts zu holen. Die Abfrage ohne `siteId`
 * abzuschicken ergäbe ein `400` und einen Fehler in einer Ansicht, die es in dem
 * Zustand ohnehin nicht gibt.
 */
export function useDevices(siteId: string | null) {
  return useQuery({
    queryKey: ["devices", siteId],
    enabled: siteId !== null,
    // Dieselbe Begründung wie bei `useDevice`: Die Liste trägt in der letzten
    // Spalte die Erreichbarkeit, und die veraltet von selbst.
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/devices", {
        params: { query: { siteId: siteId! } },
      });
      if (error || !data) throw new Error("Die Geräte konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Das **Gateway** dieser Anlage — und ob sich eines verbinden lässt (HM-109).
 *
 * **Eine Fähigkeit, kein Versuch** (Regel 10). Ohne diese Auskunft bliebe der
 * Oberfläche nur, einen Knopf zu zeigen und den Nutzer in eine Ablehnung laufen
 * zu lassen — und aus einem `409` ließe sich nicht ablesen, ob ein Gerät fehlt
 * oder schon ein Gateway hängt.
 *
 * Ohne Nachladen im Takt: Der Zustand ändert sich durch eine Handlung in dieser
 * Anwendung, und die macht ihre Abfrage selbst ungültig.
 */
export function useSiteGateway(siteId: string | null) {
  return useQuery({
    queryKey: ["site-gateway", siteId],
    enabled: siteId !== null,
    queryFn: async () => {
      const { data, error } = await api.GET("/sites/{siteId}/gateway", {
        params: { path: { siteId: siteId! } },
      });
      if (error || !data) throw new Error("Das Gateway konnte nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Was das Gateway gefunden hat — **alle Knoten, auch die abgelehnten.**
 *
 * Eine Liste, die nur die offenen zeigt, hätte keinen Weg zurück: Wer sich
 * vertan hat, findet den Knoten hier wieder.
 */
export function useGatewayNodes(gatewayId: string | null) {
  return useQuery({
    queryKey: ["gateway-nodes", gatewayId],
    enabled: gatewayId !== null,
    /**
     * Ein Fund entsteht, während jemand hinsieht — das Gateway meldet ihn,
     * sobald es ein Gerät im Netz findet. Ohne Nachladen bliebe die Seite auf
     * dem Stand ihres Öffnens stehen und der Nutzer klickte neu.
     */
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/gateways/{gatewayId}/nodes", {
        params: { path: { gatewayId: gatewayId! } },
      });
      if (error || !data) throw new Error("Die Geräte am Gateway konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Ein einzelnes Gerät für die Detailseite.
 *
 * **Nicht aus der Liste gefischt.** Wer `/anlagen/{id}/geraete/{deviceId}` neu
 * lädt oder verlinkt bekommt, hat keine Liste im Speicher — und ein Gerät, das
 * es nicht gibt, soll als solches erkennbar sein und nicht als „wird geladen"
 * hängen bleiben. Der eigene Schlüssel führt die Gerätekennung mit; das
 * `invalidateQueries` nach einer Änderung trifft über den Präfix `["devices"]`
 * auch die Liste.
 */
export function useDevice(deviceId: string | null) {
  return useQuery({
    queryKey: ["devices", "detail", deviceId],
    enabled: deviceId !== null,
    retry: false,
    /**
     * **Diese Seite zeigt einen Zustand, keinen Stammdatensatz.** Erreichbarkeit,
     * Laufzeit, Feldstärke und letzte Meldung ändern sich, während sie offen ist
     * — ohne Nachladen stünde dort die Lage von dem Augenblick, in dem sie
     * geöffnet wurde, und die Laufzeit bliebe stehen, obwohl das Gerät alle 300
     * Sekunden eine neue meldet.
     *
     * 60 Sekunden, nicht 5: Der Takt des Geräts ist die Untergrenze dessen, was
     * sich ändern **kann**, aber nicht der einzige Grund für eine Änderung — ein
     * Abbruch fällt sofort an, und die Erreichbarkeit hängt an einer Frist, die
     * hier abläuft, ohne dass eine Nachricht kommt.
     */
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/devices/{deviceId}", {
        params: { path: { deviceId: deviceId! } },
      });
      if (error || !data) throw new Error("Dieses Gerät konnte nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Nimmt die Annahmestelle gerade Gerätedaten an? (HM-056, ADR 0022)
 *
 * **Die Frage vor jeder Erreichbarkeitsmarke.** Steht die Annahme still, meldet
 * sich **jedes** Gerät scheinbar nicht mehr — und der Rat, es neu zu starten,
 * geht dann an der Ursache vorbei. Ohne diese Abfrage kann keine Ansicht den
 * Unterschied kennen.
 *
 * **Ein eigener Schlüssel, nicht unter `["devices"]`.** Die Auskunft gehört
 * keinem Gerät und keiner Anlage; sie gilt für alle gleichzeitig. Ein
 * `invalidateQueries({ queryKey: ["devices"] })` nach einer Umbenennung soll sie
 * nicht mitreißen.
 *
 * Derselbe Takt wie die Geräteabfragen: Was sie erklärt, veraltet im selben
 * Rhythmus. **`retry: false`** — schlägt sie fehl, ist `unknown` die richtige
 * Auskunft, und die zeigt ohnehin nichts an; ein Wiederholungslauf brächte nur
 * Last.
 */
export function useIntakeStatus() {
  return useQuery({
    queryKey: ["system", "intake"],
    retry: false,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/system/intake", {});
      if (error || !data) throw new Error("Der Zustand der Annahme ist nicht abrufbar.");
      return data;
    },
  });
}

/**
 * Die Datenpunkte eines Geräts (ADR 0020).
 *
 * **Eine eigene Abfrage und kein Feld an `useDevice`.** Der Datenpunkt-Katalog
 * ändert sich, wenn ein Gerät ihn meldet — die Gerätezeile im Sekundentakt, weil
 * dort Erreichbarkeit und Laufzeit stehen. Beides in einer Antwort hieße, eine
 * selten wechselnde Liste sechzigmal je Stunde mitzuschicken.
 *
 * Ohne `refetchInterval`: Ein neuer Datenpunkt entsteht beim Koppeln oder beim
 * ersten Melden, nicht laufend. Der Schlüssel hängt unter `["devices"]`, damit
 * ein `invalidateQueries` ihn mitnimmt.
 */
export function useDatapoints(deviceId: string | null) {
  return useQuery({
    queryKey: ["devices", "datapoints", deviceId],
    enabled: deviceId !== null,
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.GET("/devices/{deviceId}/datapoints", {
        params: { path: { deviceId: deviceId! } },
      });
      if (error || !data) throw new Error("Die Werte konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * **Ein Ladepunkt und seine Listen** (HM-119, ADR 0036) — ein Schlüssel für
 * alle drei, damit die einfache Seite „Laden", der Reiter „Steuerung" am Gerät
 * und die Kachel der Übersicht nach einem Auftrag gemeinsam nachladen: Zwei
 * Zustände desselben Ladepunkts widersprächen sich, sobald einer etwas schickt.
 *
 * **Schnell nachladen, solange ein Auftrag unterwegs ist.** Die Oberfläche
 * zeigt bis zur Quittung den gemeldeten Stand — und der soll dann nicht erst
 * eine halbe Minute später umspringen.
 */
const CHARGE_POINT_REFRESH_MS = 30_000;
const CHARGE_POINT_PENDING_REFRESH_MS = 3_000;

type ChargePointData = { controlBlockedBy: string | null };
function chargePointRefresh(items: readonly ChargePointData[] | undefined): number {
  return (items ?? []).some((item) => item.controlBlockedBy === "command_pending")
    ? CHARGE_POINT_PENDING_REFRESH_MS
    : CHARGE_POINT_REFRESH_MS;
}

export function useSiteChargePoints(siteId: string | null) {
  return useQuery({
    queryKey: ["charge-points", "site", siteId],
    enabled: siteId !== null,
    retry: false,
    refetchInterval: (query) => chargePointRefresh(query.state.data?.items),
    queryFn: async () => {
      const { data, error } = await api.GET("/sites/{siteId}/charge-points", {
        params: { path: { siteId: siteId! } },
      });
      if (error || !data) throw new Error("Die Ladepunkte konnten nicht geladen werden.");
      return data;
    },
  });
}

export function useUnitChargePoints(siteAreaId: string | null) {
  return useQuery({
    queryKey: ["charge-points", "unit", siteAreaId],
    enabled: siteAreaId !== null,
    retry: false,
    refetchInterval: (query) => chargePointRefresh(query.state.data?.items),
    queryFn: async () => {
      const { data, error } = await api.GET("/site-areas/{siteAreaId}/charge-points", {
        params: { path: { siteAreaId: siteAreaId! } },
      });
      if (error || !data) throw new Error("Die Ladepunkte konnten nicht geladen werden.");
      return data;
    },
  });
}

export function useChargePoint(deviceId: string | null) {
  return useQuery({
    queryKey: ["charge-points", "one", deviceId],
    enabled: deviceId !== null,
    retry: false,
    refetchInterval: (query) =>
      chargePointRefresh(query.state.data === undefined ? [] : [query.state.data]),
    queryFn: async () => {
      const { data, error } = await api.GET("/charge-points/{deviceId}", {
        params: { path: { deviceId: deviceId! } },
      });
      if (error || !data) throw new Error("Der Ladepunkt konnte nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Der zuletzt eingegangene Wert je Datenpunkt (HM-049).
 *
 * **Eine zweite Abfrage neben `useDatapoints`, und genau dafür gibt es sie.**
 * Der Katalog ändert sich, wenn ein Gerät ihn meldet; die Werte in jedem
 * Sendetakt. In einer Abfrage müsste eine von beiden im falschen Takt laufen —
 * entweder käme der Wert zu spät oder die Rohschicht würde für eine
 * unveränderte Liste befragt.
 *
 * 60 Sekunden, aus demselben Grund wie bei `useDevice`: Der Sendetakt eines
 * CTS-Geräts in der Cloud ist die Untergrenze dessen, was sich überhaupt ändern
 * kann. Häufiger zu fragen erzeugte Last für eine Zahl, die noch dieselbe ist.
 */
export function useDatapointValues(deviceId: string | null) {
  return useQuery({
    queryKey: ["devices", "datapoint-values", deviceId],
    enabled: deviceId !== null,
    retry: false,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/devices/{deviceId}/datapoints/latest", {
        params: { path: { deviceId: deviceId! } },
      });
      if (error || !data) throw new Error("Die aktuellen Werte konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Der Verlauf **eines** Datenpunkts (HM-053).
 *
 * **Das Fenster steht im Abfrageschlüssel und wird nicht bei jedem Zeichnen neu
 * gebildet.** `rangeWindow` rundet auf die volle Minute; ohne das trüge jeder
 * Durchlauf ein anderes `to`, der Schlüssel wäre jedes Mal ein neuer, und React
 * Query lüde die Reihe endlos nach.
 *
 * **Ohne `refetchInterval`.** Ein Verlauf ist eine Auswertung und keine
 * Zustandsanzeige; wer ihn offen hat, sieht den Zeitraum, den er abgefragt hat.
 * Der Momentanwert daneben kommt aus `useDatapointValues` und altert im
 * Minutentakt — das ist die Stelle, an der Aktualität etwas nützt.
 *
 * `retry: false`: Ein Textdatenpunkt wird mit `422` abgewiesen (HM-052), ein
 * gelöschter mit `404`. Beides ist eine Antwort und kein Ausfall, den ein
 * zweiter Versuch behebt.
 */
export function useDatapointHistory(
  deviceId: string | null,
  datapointId: string | null,
  window: { readonly from: string; readonly to: string },
) {
  return useQuery({
    queryKey: ["devices", "datapoint-history", deviceId, datapointId, window.from, window.to],
    enabled: deviceId !== null && datapointId !== null,
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.GET(
        "/devices/{deviceId}/datapoints/{datapointId}/history",
        {
          params: {
            path: { deviceId: deviceId!, datapointId: datapointId! },
            query: { from: window.from, to: window.to },
          },
        },
      );
      if (error || !data) throw new Error("Der Verlauf konnte nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Die Aufträge an ein Gerät und ihr Schicksal (HM-040).
 *
 * **Nachgefragt wird nur, solange etwas offen ist.** Die Quittung kommt über
 * MQTT beim Ingest an, nicht über die Antwort auf den Absendeaufruf — ohne
 * Nachfragen bliebe „abgeschickt" stehen, bis jemand die Seite neu lädt. Sobald
 * kein Auftrag mehr offen ist, hört das Nachfragen auf: Eine Seite, die im
 * Sekundentakt an einer Liste zieht, die sich nie mehr ändert, ist eine Seite,
 * die den Server beschäftigt und dem Nutzer nichts bringt.
 *
 * Der Schlüssel hängt unter `["devices"]`, damit ein `invalidateQueries` nach
 * einem abgesetzten Auftrag ihn mitnimmt.
 */
export function useDeviceCommands(deviceId: string | null) {
  return useQuery({
    queryKey: ["devices", "commands", deviceId],
    enabled: deviceId !== null,
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some(
        (command) => command.status === "pending" || command.status === "sent",
      )
        ? 3_000
        : false,
    queryFn: async () => {
      const { data, error } = await api.GET("/devices/{deviceId}/commands", {
        params: { path: { deviceId: deviceId! } },
      });
      if (error || !data) throw new Error("Die Aufträge konnten nicht geladen werden.");
      return data;
    },
  });
}

export function useIntegrations(siteId: string | null) {
  return useQuery({
    queryKey: ["integrations", siteId],
    enabled: siteId !== null,
    queryFn: async () => {
      const { data, error } = await api.GET("/integrations", {
        params: { query: { siteId: siteId! } },
      });
      if (error || !data) throw new Error("Die Integrationen konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Die Registry der Integrationstypen samt ihren **Fähigkeiten**.
 *
 * Sie beantwortet, was eine Datenquelle kann — nicht ein fehlgeschlagener
 * Aufruf (Regel 10). Ändert sich zur Laufzeit nie, deshalb ohne Wiederholung.
 */
export function useIntegrationTypes() {
  return useQuery({
    queryKey: ["integration-types"],
    queryFn: async () => {
      const { data, error } = await api.GET("/integration-types");
      if (error || !data) throw new Error("Die Integrationstypen konnten nicht geladen werden.");
      return data;
    },
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

/**
 * Der Zustand der ganzen Installation — nur für Plattform-Admins.
 *
 * **Alle zehn Sekunden neu**, wie der Health-Endpunkt: Eine Seite, die einen
 * Zustand zeigt und ihn nie wieder holt, zeigt die Lage von dem Augenblick, in
 * dem sie geöffnet wurde (HM-042). Und gerade hier sitzt jemand davor, während
 * er einen Dienst neu startet.
 *
 * `retry: false`: Wer keine Admin-Rolle hat, bekommt `403`, und das ändert sich
 * durch Wiederholen nicht.
 */
export function useSystemStatus() {
  return useQuery({
    queryKey: ["system-status"],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/system");
      if (error || !data) throw new Error("Der Systemzustand ließ sich nicht laden.");
      return data;
    },
    refetchInterval: 10_000,
    retry: false,
  });
}

/**
 * Die Nutzungsstatistik für den Plattform-Admin (HM-168). Kein Nachladen im
 * Takt: Die Tageszahlen rechnet der Worker alle Viertelstunde, und eine Seite,
 * die alle zehn Sekunden dieselben Zahlen holt, misst vor allem sich selbst.
 */
export function useUsageReport(days: number, platform: "web" | "android" | "ios" | null) {
  return useQuery({
    queryKey: ["admin-usage", days, platform],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/usage", {
        params: { query: { days, ...(platform === null ? {} : { platform }) } },
      });
      if (error || !data) throw new Error("Die Nutzungsstatistik ließ sich nicht laden.");
      return data;
    },
    retry: false,
  });
}

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/health");
      // Der Health-Endpunkt antwortet bei Störungen mit 503 und trotzdem mit
      // brauchbarem Körper — das ist kein Fehlerfall für die Anzeige.
      if (data) return data;
      if (response.status === 503) return (error ?? null) as never;
      throw new Error(`Die API antwortete mit ${response.status}.`);
    },
    refetchInterval: 10_000,
    retry: false,
  });
}

/**
 * Die Messkonzept-Vorlagen.
 *
 * **Zurzeit von keiner Ansicht benutzt** und trotzdem hier: Der Endpunkt steht,
 * und die Abfrage gehört zu ihm. Die Liste stand unter „System" und ist wieder
 * heraus — ein Katalog zum Ansehen, aus dem man nichts machen kann, ist keine
 * Seite wert. Sie kehrt mit HM-012 zurück, dort wo aus einer Vorlage ein
 * Messkonzept wird.
 */
export function useConceptTemplates() {
  return useQuery({
    queryKey: ["concept-templates"],
    queryFn: async () => {
      const { data, error } = await api.GET("/concept-templates");
      if (error) throw new Error("Die Vorlagen konnten nicht geladen werden.");
      return data;
    },
    retry: false,
  });
}

/**
 * Die Zähler einer **Anlage** (HM-057).
 *
 * Derselbe Schnitt wie bei Geräten und Integrationen: Ein Zähler gehört einer
 * Anlage, und der Schlüssel trägt sie mit — ein Wechsel im Umschalter lädt die
 * Liste neu, statt die vorige weiterzuzeigen.
 *
 * **Ohne `refetchInterval`.** Ein Zähler ist ein Stammdatensatz; er ändert sich,
 * wenn jemand ihn ändert oder ein Gerät zum ersten Mal Werte liefert — nicht
 * laufend. Was sich im Minutentakt ändert, sind seine Werte, und die stehen
 * hier nicht.
 */
export function useMeters(siteId: string | null, includeInactive = false) {
  return useQuery({
    queryKey: ["meters", siteId, includeInactive],
    enabled: siteId !== null,
    queryFn: async () => {
      const { data, error } = await api.GET("/meters", {
        params: {
          query: { siteId: siteId!, ...(includeInactive ? { includeInactive: true } : {}) },
        },
      });
      if (error || !data) throw new Error("Die Zähler konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Ein einzelner Zähler für die Detailseite.
 *
 * **Nicht aus der Liste gefischt** — dieselbe Begründung wie bei `useDevice`:
 * Wer die Adresse neu lädt oder verlinkt bekommt, hat keine Liste im Speicher,
 * und ein Zähler, den es nicht gibt, soll als solcher erkennbar sein statt als
 * „wird geladen" hängen zu bleiben.
 */
export function useMeter(meterId: string | null) {
  return useQuery({
    queryKey: ["meters", "detail", meterId],
    enabled: meterId !== null,
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.GET("/meters/{meterId}", {
        params: { path: { meterId: meterId! } },
      });
      if (error || !data) throw new Error("Dieser Zähler konnte nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Die Messstellen einer Anlage.
 *
 * Gebraucht beim Anlegen von Hand: Ein Zähler gehört an eine Messstelle, und
 * die Wahl zwischen „an eine bestehende" und „an eine neue" ist die erste, die
 * das Formular stellt.
 */
export function useMeteringPoints(siteId: string | null) {
  return useQuery({
    queryKey: ["metering-points", siteId],
    enabled: siteId !== null,
    queryFn: async () => {
      const { data, error } = await api.GET("/metering-points", {
        params: { query: { siteId: siteId! } },
      });
      if (error || !data) throw new Error("Die Messstellen konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Die Wohneinheiten einer Anlage (HM-079).
 *
 * **Ohne `refetchInterval`**, wie die Zähler: Eine Wohneinheit ändert sich,
 * wenn jemand sie ändert. Was sich im Takt ändert, sind ihre Werte, und die
 * kommen mit HM-080 aus einer eigenen Abfrage.
 */
export function useSiteAreas(siteId: string | null) {
  return useQuery({
    queryKey: ["site-areas", siteId],
    enabled: siteId !== null,
    queryFn: async () => {
      const { data, error } = await api.GET("/sites/{siteId}/site-areas", {
        params: { path: { siteId: siteId! } },
      });
      if (error || !data) throw new Error("Die Wohneinheiten konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Eine einzelne Wohneinheit für die Detailseite — nicht aus der Liste
 * gefischt, aus demselben Grund wie bei Zähler und Gerät: Wer die Adresse neu
 * lädt, hat keine Liste im Speicher.
 */
/**
 * Die Erzeugungsanlagen einer Anlage (HM-122) — Stammdaten wie die
 * Wohneinheiten, ohne `refetchInterval`: Eine PV-Anlage ändert sich, wenn
 * jemand sie ändert.
 */
export function useGenerationAssets(siteId: string | null) {
  return useQuery({
    queryKey: ["generation-assets", siteId],
    enabled: siteId !== null,
    queryFn: async () => {
      const { data, error } = await api.GET("/sites/{siteId}/generation-assets", {
        params: { path: { siteId: siteId! } },
      });
      if (error || !data) throw new Error("Die Erzeugungsanlagen konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Eine einzelne Erzeugungsanlage (HM-131) — für die Detailseite **und** die
 * Kopfleiste des Rahmens, unter demselben Schlüssel: kein zweiter Abruf.
 */
export function useGenerationAsset(assetId: string | null) {
  return useQuery({
    queryKey: ["generation-assets", "detail", assetId],
    enabled: assetId !== null,
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.GET("/generation-assets/{generationAssetId}", {
        params: { path: { generationAssetId: assetId! } },
      });
      if (error || !data) throw new Error("Die Erzeugungsanlage konnte nicht geladen werden.");
      return data;
    },
  });
}

/** Der Verlauf einer Erzeugungsanlage aus ihren Geräten (HM-131) — Fenster wie am Datenpunkt. */
export function useGenerationAssetHistory(
  assetId: string | null,
  window: { readonly from: string; readonly to: string },
) {
  return useQuery({
    queryKey: ["generation-assets", "history", assetId, window.from, window.to],
    enabled: assetId !== null,
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.GET("/generation-assets/{generationAssetId}/history", {
        params: {
          path: { generationAssetId: assetId! },
          query: { from: window.from, to: window.to },
        },
      });
      if (error || !data) throw new Error("Der Verlauf konnte nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Was eine Erzeugungsanlage über ihr Gerät meldet (HM-129). Lädt sich nach wie
 * der Zustand des Geräts selbst — eine Leistung von vor einer Stunde ist keine.
 */
export function useGenerationAssetState(assetId: string | null) {
  return useQuery({
    queryKey: ["generation-assets", "state", assetId],
    enabled: assetId !== null,
    retry: false,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/generation-assets/{generationAssetId}/state", {
        params: { path: { generationAssetId: assetId! } },
      });
      if (error || !data) throw new Error("Der Zustand der Anlage konnte nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Der Zustand eines Speichers (HM-122) — gedeutet in der API. Im Takt der
 * Geräteseite, denn Ladezustand und Leistung ändern sich, während sie offen ist.
 */
/** Der Zustand eines evcc-Standorts (HM-165) — wie der Speicher, aber mit Einstellungen. */
export function useEvccSiteState(deviceId: string | null) {
  return useQuery({
    queryKey: ["devices", "evcc-site", deviceId],
    enabled: deviceId !== null,
    retry: false,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/devices/{deviceId}/evcc-site", {
        params: { path: { deviceId: deviceId! } },
      });
      if (error || !data) throw new Error("Der Standort konnte nicht geladen werden.");
      return data;
    },
  });
}

export function useStorageState(deviceId: string | null) {
  return useQuery({
    queryKey: ["devices", "storage", deviceId],
    enabled: deviceId !== null,
    retry: false,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/devices/{deviceId}/storage", {
        params: { path: { deviceId: deviceId! } },
      });
      if (error || !data) throw new Error("Der Speicher konnte nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Der Zustand eines Wechselrichters (HM-126) — PV, Hybrid oder Batterie,
 * gedeutet in der API. Im Takt der Geräteseite, wie der Speicher.
 */
export function useInverterState(deviceId: string | null) {
  return useQuery({
    queryKey: ["devices", "inverter", deviceId],
    enabled: deviceId !== null,
    retry: false,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/devices/{deviceId}/inverter", {
        params: { path: { deviceId: deviceId! } },
      });
      if (error || !data) throw new Error("Der Wechselrichter konnte nicht geladen werden.");
      return data;
    },
  });
}

export function useSiteArea(siteAreaId: string | null) {
  return useQuery({
    queryKey: ["site-areas", "detail", siteAreaId],
    enabled: siteAreaId !== null,
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.GET("/site-areas/{siteAreaId}", {
        params: { path: { siteAreaId: siteAreaId! } },
      });
      if (error || !data) throw new Error("Diese Wohneinheit konnte nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Die offenen **Anlagevorschläge** einer Anlage (HM-058).
 *
 * **Mit `refetchInterval`, anders als die Zählerliste.** Ein Vorschlag entsteht,
 * während jemand die Seite offen hat: Er koppelt ein Gerät, wartet auf den
 * ersten Sendetakt, und ohne Nachladen bliebe die Seite leer — genau in dem
 * Augenblick, für den sie gebaut ist. 30 Sekunden, weil der erste Wert eines
 * frisch gekoppelten Geräts nach spätestens einem Sendetakt kommt.
 */
export function useMeterSuggestions(siteId: string | null) {
  return useQuery({
    queryKey: ["meter-suggestions", siteId],
    enabled: siteId !== null,
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/meter-suggestions", {
        params: { query: { siteId: siteId! } },
      });
      if (error || !data) throw new Error("Die Vorschläge konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Die Messkonzepte einer Anlage — alle Fassungen, die jüngste zuerst (HM-012).
 *
 * **Ohne `refetchInterval`**: Ein Konzept ändert sich, wenn jemand es ändert.
 * Was sich im Takt ändert, sind seine Werte, und die kommen aus `useSiteEnergy`.
 */
export function useSiteConcepts(siteId: string | null) {
  return useQuery({
    queryKey: ["concepts", siteId],
    enabled: siteId !== null,
    queryFn: async () => {
      const { data, error } = await api.GET("/sites/{siteId}/concepts", {
        params: { path: { siteId: siteId! } },
      });
      if (error || !data) throw new Error("Die Messkonzepte konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Bewohner und offene Einladungen einer Wohneinheit (HM-081) — für den
 * Vermieter. Ohne `refetchInterval`: Eine Annahme ändert sich, wenn jemand sie
 * annimmt, und wer die Liste offen hat, hat gerade eingeladen.
 */
export function useUnitResidents(siteAreaId: string | null) {
  return useQuery({
    queryKey: ["unit-residents", siteAreaId],
    enabled: siteAreaId !== null,
    queryFn: async () => {
      const { data, error } = await api.GET("/site-areas/{siteAreaId}/residents", {
        params: { path: { siteAreaId: siteAreaId! } },
      });
      if (error || !data) throw new Error("Die Bewohner konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Die Wohnungen des angemeldeten Nutzers — über alle Accounts hinweg (HM-081).
 * `retry: false`: Ein `403` für ein Konto, das nirgends wohnt, ist keine Störung.
 */
export function useMyUnits() {
  return useQuery({
    queryKey: ["me", "units"],
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.GET("/me/units", {});
      if (error || !data) throw new Error("Deine Wohnungen konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Die Anlagen, die mit dem angemeldeten Nutzer geteilt sind — über alle
 * Accounts hinweg (HM-160). `retry: false`: Wer keine hat, bekommt eine leere
 * Liste; ein Fehler soll den Rahmen nicht aufhalten.
 */
export function useSharedSites() {
  return useQuery({
    queryKey: ["me", "shared-sites"],
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.GET("/me/shared-sites", {});
      if (error || !data) throw new Error("Die geteilten Anlagen konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Offene Einladungen an die eigene Adresse (#47) — beide Arten, über alle
 * Accounts hinweg. Liegt unter `["me", …]`: Nach dem Annehmen lädt ein
 * `invalidateQueries({ queryKey: ["me"] })` Einladungen, geteilte Anlagen und
 * Wohnungen zusammen nach.
 */
export function useMyInvitations() {
  return useQuery({
    queryKey: ["me", "invitations"],
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.GET("/me/invitations", {});
      if (error || !data) throw new Error("Deine Einladungen konnten nicht geladen werden.");
      return data;
    },
  });
}

/** Besitzer, Freigaben und offene Einladungen einer Anlage — nur für den Besitzer (HM-160). */
export function useSiteShares(siteId: string | null) {
  return useQuery({
    queryKey: ["site-shares", siteId],
    enabled: siteId !== null,
    queryFn: async () => {
      const { data, error } = await api.GET("/sites/{siteId}/shares", {
        params: { path: { siteId: siteId! } },
      });
      if (error || !data) throw new Error("Die Freigaben konnten nicht geladen werden.");
      return data;
    },
  });
}

/** Die Vorschau einer Einladung — ohne Anmeldung (HM-081). */
export function useInvitationPreview(token: string | null) {
  return useQuery({
    queryKey: ["invitation", token],
    enabled: token !== null,
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.GET("/invitations/{token}", {
        params: { path: { token: token! } },
      });
      if (error || !data)
        throw new Error("Diese Einladung ist unbekannt, abgelaufen oder wurde zurückgenommen.");
      return data;
    },
  });
}

/**
 * Die Vorlage einer Fassung, expandiert (HM-080) — mit den Steckplätzen, die
 * aus den Wohneinheiten und Verbrauchern **dieser Fassung** entstanden sind.
 * Die Web-App darf die Engine nicht importieren; sie bekommt sie fertig.
 */
export function useConceptTemplate(conceptId: string | null) {
  return useQuery({
    queryKey: ["concepts", "template", conceptId],
    enabled: conceptId !== null,
    queryFn: async () => {
      const { data, error } = await api.GET("/concepts/{conceptId}/template", {
        params: { path: { conceptId: conceptId! } },
      });
      if (error || !data)
        throw new Error("Die Vorlage des Messkonzepts konnte nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Die Werte einer Wohneinheit aus dem Messkonzept ihrer Anlage (HM-080) —
 * dieselbe Form wie `useSiteEnergy`, im selben Takt nachgeladen.
 */
export function useUnitEnergy(siteAreaId: string | null, query: EnergyPeriodQuery | null) {
  return useQuery({
    queryKey: ["unit-energy", siteAreaId, query?.period, query?.date],
    enabled: siteAreaId !== null && query !== null,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/site-areas/{siteAreaId}/energy", {
        params: {
          path: { siteAreaId: siteAreaId! },
          query: { period: query!.period, ...(query!.date === null ? {} : { date: query!.date }) },
        },
      });
      if (error || !data)
        throw new Error("Die Werte der Wohneinheit konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Die Werte einer Anlage aus ihrem Messkonzept über einen Kalenderzeitraum
 * (HM-012, HM-076).
 *
 * **Zeitraum und Anker stehen im Schlüssel, nicht das Fenster.** Der Server
 * löst „diese Woche" in der Zeitzone der Anlage auf und sagt in `period`, was
 * davor und danach kommt — die Kopfleiste und die Seite teilen sich denselben
 * Eintrag im Zwischenspeicher. Nachgeladen wird jede Minute: Der Worker rechnet
 * im Fünf-Minuten-Takt, und eine Seite, die einen Stand zeigt und ihn nie
 * wieder holt, zeigt den Augenblick ihres Öffnens.
 */
export interface EnergyPeriodQuery {
  readonly period: "day" | "week" | "month" | "year";
  /** Kalenderdatum der Anlage; `null` heißt der aktuelle Zeitraum. */
  readonly date: string | null;
}

export function useSiteEnergy(siteId: string | null, query: EnergyPeriodQuery | null) {
  return useQuery({
    queryKey: ["site-energy", siteId, query?.period, query?.date],
    enabled: siteId !== null && query !== null,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/sites/{siteId}/energy", {
        params: {
          path: { siteId: siteId! },
          query: { period: query!.period, ...(query!.date === null ? {} : { date: query!.date }) },
        },
      });
      if (error || !data) throw new Error("Die Werte der Anlage konnten nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Die Leistung der Zähler im Messkonzept über denselben Zeitraum (HM-076).
 * Nur geholt, wenn die Ansicht „Leistung" zeigt — sie liest die Rohschicht,
 * und die kostet mehr als die materialisierten Energiebuckets.
 */
/** Die Leistung der Zähler einer Wohneinheit (HM-083) — nur geladen, wenn die Ansicht sie zeigt. */
export function useUnitPower(
  siteAreaId: string | null,
  query: EnergyPeriodQuery | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["unit-power", siteAreaId, query?.period, query?.date],
    enabled: enabled && siteAreaId !== null && query !== null,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/site-areas/{siteAreaId}/power", {
        params: {
          path: { siteAreaId: siteAreaId! },
          query: { period: query!.period, ...(query!.date === null ? {} : { date: query!.date }) },
        },
      });
      if (error || !data)
        throw new Error("Die Leistung der Wohneinheit konnte nicht geladen werden.");
      return data;
    },
  });
}

/** Die Momentanwerte einer Wohneinheit samt ihren Zählern (HM-083). */
export function useUnitLive(siteAreaId: string | null) {
  return useQuery({
    queryKey: ["unit-live", siteAreaId],
    enabled: siteAreaId !== null,
    refetchInterval: 15_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/site-areas/{siteAreaId}/live", {
        params: { path: { siteAreaId: siteAreaId! } },
      });
      if (error || !data)
        throw new Error("Die Momentanwerte der Wohneinheit konnten nicht geladen werden.");
      return data;
    },
  });
}

export function useSitePower(
  siteId: string | null,
  query: EnergyPeriodQuery | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["site-power", siteId, query?.period, query?.date],
    enabled: enabled && siteId !== null && query !== null,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/sites/{siteId}/power", {
        params: {
          path: { siteId: siteId! },
          query: { period: query!.period, ...(query!.date === null ? {} : { date: query!.date }) },
        },
      });
      if (error || !data) throw new Error("Die Leistung konnte nicht geladen werden.");
      return data;
    },
  });
}

/**
 * Die Momentanwerte einer Anlage — der letzte Wert jedes gebundenen Zählwerks
 * (HM-012, aus HM-010 vorgezogen).
 *
 * **Alle 15 Sekunden**, weil das der Grund der Ansicht ist: Wer die Anlage
 * gerade eingerichtet hat, will sehen, dass etwas ankommt — und die erste
 * Viertelstunde des Messkonzepts kommt erst, wenn sie geschlossen ist.
 */
export function useSiteLive(siteId: string | null) {
  return useQuery({
    queryKey: ["site-live", siteId],
    enabled: siteId !== null,
    refetchInterval: 15_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/sites/{siteId}/live", {
        params: { path: { siteId: siteId! } },
      });
      if (error || !data) throw new Error("Die Momentanwerte konnten nicht geladen werden.");
      return data;
    },
  });
}

/** Was eine Meldung aus dieser Sitzung mitnähme (HM-169) — die Vorschau im Formular. */
export function useFeedbackContext(page: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["feedback-context", page],
    queryFn: async () => {
      const { data, error } = await api.GET("/feedback/context", {
        params: { query: page === null ? {} : { page } },
      });
      if (error || !data) throw new Error("Die mitgesendeten Angaben ließen sich nicht laden.");
      return data;
    },
    enabled,
    retry: false,
  });
}

/** Das Feedback aller Nutzer für den Plattform-Admin (HM-169). */
export function useAdminFeedback(kind: "bug" | "wish" | null, status: "open" | "accepted" | "done" | "rejected" | null) {
  return useQuery({
    queryKey: ["admin-feedback", kind, status],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/feedback", {
        params: { query: { ...(kind === null ? {} : { kind }), ...(status === null ? {} : { status }) } },
      });
      if (error || !data) throw new Error("Das Feedback ließ sich nicht laden.");
      return data;
    },
    retry: false,
  });
}

export function useAdminFeedbackItem(feedbackId: string | null) {
  return useQuery({
    queryKey: ["admin-feedback-item", feedbackId],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/feedback/{feedbackId}", {
        params: { path: { feedbackId: feedbackId ?? "" } },
      });
      if (error || !data) throw new Error("Die Meldung ließ sich nicht laden.");
      return data;
    },
    enabled: feedbackId !== null,
    retry: false,
  });
}
